import md5 from 'js-md5'
import { nextTick } from 'vue'
import { LoadingBar } from 'view-ui-plus'

LoadingBar.config({ color: '#4275ef', height: 1 })
let activeRequests = 0
let progressHideTimer
const requestsInFlight = new Map()
const requestBursts = new Map()
let readGeneration = 0

const prefix = process.env.VUE_APP_USER_API_URL || '/api/user'
export const tokenStorageKey = 'WJ_ACCESS_TOKEN'
const previousToken = localStorage.getItem('weijie-user-token')
if (previousToken && !localStorage.getItem(tokenStorageKey)) {
  localStorage.setItem(tokenStorageKey, previousToken)
}
localStorage.removeItem('weijie-user-token')
const encoder = new TextEncoder()
const base64 = (bytes) => {
  const array = new Uint8Array(bytes)
  let binary = ''
  for (let offset = 0; offset < array.length; offset += 32768) {
    binary += String.fromCharCode(...array.subarray(offset, offset + 32768))
  }
  return btoa(binary)
}
export const signMobile = (phone) => phone.slice(0, 3)
  + phone
    .slice(3)
    .split('')
    .map((digit) => '2176548930'[Number(digit)])
    .join('')
export const legacyProof = (password, phone, time) => md5(md5(md5(password + phone)) + time)
export class AuthApiError extends Error {
  constructor(result) {
    super(result.message)
    this.code = result.code
    this.data = result.data || {}
  }
}
function retryDelay(value) {
  const seconds = Number(value) || Math.ceil((Date.parse(value) - Date.now()) / 1000)
  return Number.isFinite(seconds) ? Math.max(0, seconds) : 0
}
async function read(response, notifyExpiry = true, expiryEvent = 'weijie-auth-expired') {
  let result
  try {
    result = await response.json()
  } catch {
    const error = new Error('服务暂不可用，请稍后重试')
    error.status = response.status
    error.retryAfter = retryDelay(response.headers.get('Retry-After'))
    throw error
  }
  if (!response.ok || result.code !== 'OK') {
    if (notifyExpiry && ['UNAUTHENTICATED', 'TOKEN_EXPIRED', 'TOKEN_REVOKED'].includes(result.code)) {
      window.dispatchEvent(new CustomEvent(expiryEvent))
    }
    const error = new AuthApiError(result)
    error.status = response.status
    error.retryAfter = retryDelay(result.data?.retry_after ?? response.headers.get('Retry-After'))
    throw error
  }
  return result.data
}
export async function request(action, data, options = {}) {
  const method = data ? 'POST' : 'GET'
  const context = JSON.stringify([options.prefix || prefix, options.tokenStorageKey || tokenStorageKey,
    localStorage.getItem(options.tokenStorageKey || tokenStorageKey), Object.entries(options.headers || {}).sort()])
  const endpointKey = JSON.stringify([context, method, action])
  // Nonces and CAPTCHA/device reads must retain their separate response identity.
  const independent = !data && ['key', 'image', 'device'].includes(action.split('?')[0])
  const payloadId = data ? md5(JSON.stringify(data)) : null
  const flightKey = JSON.stringify([endpointKey, payloadId, data ? null : readGeneration])
  const pending = !independent && requestsInFlight.get(flightKey)
  if (pending) {
    if (data) throw new AuthApiError({ code: 'REQUEST_IN_PROGRESS', message: '正在提交，请勿重复操作' })
    return JSON.parse(JSON.stringify(await pending))
  }
  const now = Date.now()
  for (const [key, state] of requestBursts) {
    if (now - state.lastUsed > 10000 && state.blockedUntil <= now) requestBursts.delete(key)
  }
  let burst
  if (!independent) {
    burst = requestBursts.get(endpointKey) || { start: now, count: 0, payloadId, blockedUntil: 0, lastUsed: now }
    requestBursts.set(endpointKey, burst)
    burst.lastUsed = now
    if (burst.payloadId !== payloadId || now - burst.start >= 2000) { burst.start = now; burst.count = 0; burst.payloadId = payloadId }
    if (burst.blockedUntil > now || ++burst.count > 5) {
      burst.blockedUntil = Math.max(burst.blockedUntil, now + 10000)
      throw new AuthApiError({ code: 'REQUEST_LOOP_BLOCKED', message: '该操作重复请求过多，已暂停，请稍后重试' })
    }
  }
  if (data) readGeneration += 1
  // eslint-disable-next-line no-use-before-define
  const task = performRequest(action, data, options)
  if (!independent) requestsInFlight.set(flightKey, task)
  clearTimeout(progressHideTimer)
  if (!activeRequests) LoadingBar.start()
  activeRequests += 1
  try {
    return await task
  } catch (error) {
    if (burst && (error.code === 'RATE_LIMITED' || error.status === 429)) {
      burst.blockedUntil = Date.now() + Math.max(10, error.retryAfter || 0) * 1000
    }
    throw error
  } finally {
    // A post-save read must not join a GET started before/during the mutation,
    // including an uncertain write whose response was lost.
    if (data) readGeneration += 1
    if (!independent && requestsInFlight.get(flightKey) === task) requestsInFlight.delete(flightKey)
    await nextTick()
    activeRequests -= 1
    if (!activeRequests) {
      LoadingBar.update(100)
      // Own the hide timer so a new request can cancel it before the bar disappears.
      progressHideTimer = setTimeout(() => LoadingBar.destroy(), 300)
    }
  }
}

async function apiFetch(url, options) {
  options = { ...options, headers: { ...options.headers, 'X-WJ-Site-Origin': window.location.origin } }
  const target = new URL(url, window.location.href)
  if (target.origin === window.location.origin) return fetch(url, options)
  const key = target.origin + target.pathname.split('/').slice(0, 3).join('/')
  const session = sessionStorage.getItem(`${key}:verification`)
  const device = localStorage.getItem(`${key}:device`)
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      ...(session ? { 'X-WJ-Verification-Session': session } : {}),
      ...(device ? { 'X-WJ-Device': device } : {}),
    }
  })
  const nextSession = response.headers.get('X-WJ-Verification-Session')
  const nextDevice = response.headers.get('X-WJ-Device')
  if (nextSession) sessionStorage.setItem(`${key}:verification`, nextSession)
  if (nextDevice) localStorage.setItem(`${key}:device`, nextDevice)
  return response
}

async function performRequest(action, data, options) {
  const endpoint = options.prefix || prefix
  const token = localStorage.getItem(options.tokenStorageKey || tokenStorageKey)
  const authHeaders = { ...options.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  if (!data) {
    return read(
      await apiFetch(`${endpoint}/${action}`, {
        credentials: 'include', cache: 'no-store', headers: authHeaders,
      }),
      action !== 'me',
      options.expiryEvent,
    )
  }
  const keyData = await performRequest('key', undefined, options)
  const pem = keyData.public_key.replace(/-----[^-]+-----/g, '').replace(/\s/g, '')
  const publicKey = await crypto.subtle.importKey(
    'spki',
    Uint8Array.from(atob(pem), (c) => c.charCodeAt(0)),
    { name: 'RSA-OAEP', hash: 'SHA-1' },
    false,
    ['encrypt'],
  )
  const aes = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt'])
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const payload = { ...data }
  if (payload.phone) {
    payload.mobile = signMobile(payload.phone)
    delete payload.phone
  }
  if (action === 'login') {
    payload.t = keyData.server_time
    payload.password = legacyProof(payload.password, data.phone, payload.t)
  }
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: encoder.encode(keyData.nonce) },
    aes,
    encoder.encode(JSON.stringify(payload)),
  )
  const wrapped = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    await crypto.subtle.exportKey('raw', aes),
  )
  return read(
    await apiFetch(`${endpoint}/${action}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        ...(payload.mobile ? { mobile: payload.mobile } : {}),
        nonce: keyData.nonce,
        key: base64(wrapped),
        iv: base64(iv),
        data: base64(cipher),
      }),
    }),
    true,
    options.expiryEvent,
  )
}
